import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { Zap, Battery, BarChart3, ExternalLink } from "lucide-react";


interface ServiceCard {
  label: string;
  title: string;
  text: string;
  ctaLabel: string;
  ctaHref: string;
}

const cards: ServiceCard[] = [
  {
    label: "MIETERSTROM",
    title: "MIETERSTROM FÜR MEHRFAMILIEN-HÄUSER",
    text: "Hausverwaltungen und Eigentümer profitieren von gemeinschaftlicher Solarnutzung. Wir installieren, G2 vermarktet — fair verteilt, transparent abgerechnet.",
    ctaLabel: "Mieterstrom mit\nG2 entdecken→",
    ctaHref: "https://g2-contracting.de/mieterstrom",
  },
  {
    label: "CONTRACTING SERVICES",
    title: "Energie als Service",
    text: "Anlage von uns, Vermarktung über G2, Rendite für Sie. Erst verdienen — dann zahlen. Ideal für Eigentümer und Gewerbe ohne Investitionsrisiko.",
    ctaLabel: "Contracting\nkennenlernen →",
    ctaHref: "https://g2-contracting.de/contracting",
  },
  {
    label: "ENERGIE-MANAGEMENT",
    title: "PV, SPEICHER & WALLBOX INTELLIGENT GESTEUERT",
    text: "G2 steuert dynamisch: PV-Überschuss in den Speicher oder ins E-Auto, Strom-Zukauf nur zu Niedrigtarif-Stunden. Maximale Eigenverbrauchsquote, minimale Stromkosten.",
    ctaLabel: "Energiemanagement\nentdecken →",
    ctaHref: "https://g2-contracting.de/ems",
  },
];

const caseImage =
  "https://www.zweering-elektrotechnik.de/wp-content/uploads/2026/01/FOTOS_-_SOCIAL_MEDIA_20241112_073801140-Kopie.jpg";

const schemaRows = [
  { Icon: Zap, label: "PV-Anlage erzeugt Strom" },
  { Icon: Battery, label: "G2 optimiert den Stromfluss" },
  { Icon: BarChart3, label: "Das Managementsystem, dass mitdenkt" },
];

export default function G2Leistungen() {
  useSmoothScroll();

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "Archivo, sans-serif" }}>
      <Seo
        title="G2 Contracting — Leistungen | Zweering Elektrotechnik Emmerich am Rhein"
        description="Leistungen von G2 Contracting: Mieterstrom, Contracting-Services und Energie-Management für Immobilien in Emmerich am Rhein und Umgebung."
        canonical="https://zweering.de/g2-contracting/leistungen"
        breadcrumbs={[
          { name: "Startseite", url: "https://zweering.de/" },
          { name: "G2 Contracting", url: "https://zweering.de/g2-contracting" },
          { name: "Leistungen", url: "https://zweering.de/g2-contracting/leistungen" },
        ]}
      />
      <Header />



      <main id="main-content">
        {/* HERO */}
        <section className="relative pt-40 pb-24 md:pt-48 md:pb-32 px-6 md:px-10 lg:px-14 overflow-hidden bg-[#0D1F12]">
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              backgroundImage:
                "linear-gradient(rgba(76,175,80,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(76,175,80,0.08) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          <div className="relative max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center">
            <div>
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-6 font-medium">
                G2 CONTRACTING · LEISTUNGEN
              </p>
              <h1
                className="font-bold uppercase text-white leading-[0.95] break-words"
                style={{
                  fontSize: "clamp(1.75rem, 7vw, 4.5rem)",
                  letterSpacing: "clamp(-0.6px, -0.18vw, -2.4px)",
                  textShadow: "0 0 36px rgba(76,175,80,0.35)",
                }}
              >
                ENERGIE ALS GESCHÄFTS-MODELL
              </h1>
              <p className="text-white/80 text-base md:text-xl mt-6 max-w-2xl leading-relaxed">
                Mieterstrom, Contracting, Energiemanagement — die Software-Seite von Zweering Elektrotechnik.
              </p>
            </div>

            {/* Mini-Schema-Card */}
            <div className="w-full md:justify-self-end">
              <div
                className="w-full rounded-2xl p-8"
                style={{
                  maxWidth: "480px",
                  backgroundColor: "#141414",
                  border: "1px solid rgba(76,175,80,0.2)",
                }}
              >
                <p className="text-[#4CAF50] uppercase text-xs tracking-widest font-medium mb-6">
                  SO FUNKTIONIERT G2
                </p>
                <div>
                  {schemaRows.map((r, i) => (
                    <div
                      key={r.label}
                      className="flex items-center gap-4 py-4"
                      style={{
                        borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <r.Icon size={20} className="text-[#4CAF50] shrink-0" />
                      <span className="flex-1 text-white text-sm md:text-base">{r.label}</span>
                      <span
                        aria-hidden
                        className="inline-block w-2 h-2 rounded-full bg-[#4CAF50] shadow-[0_0_10px_rgba(76,175,80,0.8)]"
                        style={{ animation: "pulse 2s ease-in-out infinite" }}
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-white/50 text-xs">142 Anlagen aktiv · Live</p>
              </div>
            </div>
          </div>
        </section>

        {/* CARDS */}
        <section className="bg-[#0A0A0A] py-28 md:py-36 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {cards.map((c) => (
              <article
                key={c.label}
                className="flex flex-col rounded-2xl p-8 md:p-10 hover:-translate-y-1"
                style={{
                  backgroundColor: "rgba(255,255,255,0.04)",
                  boxShadow:
                    "0 0 0 1px rgba(76,175,80,0.15), 0 24px 48px rgba(0,0,0,0.4)",
                  transition: "all 300ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 0 0 1px rgba(76,175,80,0.4), 0 32px 64px rgba(0,0,0,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 0 0 1px rgba(76,175,80,0.15), 0 24px 48px rgba(0,0,0,0.4)";
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span
                    aria-hidden
                    className="inline-block w-2.5 h-2.5 rounded-full bg-[#4CAF50] shadow-[0_0_12px_rgba(76,175,80,0.7)]"
                  />
                  <span className="text-[#4CAF50] uppercase text-xs tracking-widest font-medium">
                    {c.label}
                  </span>
                </div>
                <h2
                  className="font-bold uppercase text-white leading-[1.05] break-words"
                  style={{
                    fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)",
                    letterSpacing: "-0.6px",
                  }}
                >
                  {c.title}
                </h2>
                <p className="mt-5 text-white/70 text-base leading-relaxed flex-1">{c.text}</p>
                <a
                  href={c.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-8 px-6 py-3.5 rounded-full text-sm font-medium bg-[#4CAF50] text-[#0A0A0A] hover:bg-[#5BC75F] transition-colors self-start whitespace-pre-line"
                >
                  {c.ctaLabel}
                </a>
              </article>
            ))}
          </div>
        </section>

        {/* HARDWARE-BRÜCKE */}
        <section className="bg-[#0D1F12] py-28 md:py-36 px-6 md:px-10 lg:px-14">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-8 font-medium">
              HARDWARE + SOFTWARE
            </p>
            <h2
              className="font-bold uppercase text-white leading-[1.05] break-words"
              style={{
                fontSize: "clamp(1.75rem, 5vw, 3.25rem)",
                letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
                textShadow: "0 0 32px rgba(76,175,80,0.4)",
              }}
            >
              ZWEI ETAGEN — EIN HAUS.
              <br />
              <span className="text-[#4CAF50]">ZWEERING BAUT DIE HARDWARE.</span>
              <br />
              G2 MACHT SIE ERTRAGREICH.
            </h2>
            <p className="mt-8 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Wir installieren Photovoltaik, Speicher und Wallbox. G2 Contracting bringt das System ans Strommarkt-Netz.
            </p>
          </div>
        </section>

        {/* CASE TEASER */}
        <section className="bg-[#0A0A0A] py-28 md:py-36 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div
              className="overflow-hidden aspect-[4/3] rounded-2xl"
              style={{
                backgroundColor: "#0A0A0A",
                border: "1px solid rgba(76,175,80,0.2)",
              }}
            >
              <img
                src={caseImage}
                alt="Mehrfamilienhaus in Emmerich am Rhein mit 47 kWp Photovoltaik-Anlage, Mieterstrom über G2 vermarktet"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                REFERENZ
              </p>
              <h2
                className="font-bold uppercase text-white leading-[0.95] break-words"
                style={{
                  fontSize: "clamp(1.25rem, 5vw, 3rem)",
                  letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
                  textShadow: "0 0 28px rgba(76,175,80,0.35)",
                }}
              >
                MIETERSTROM IN DER PRAXIS
              </h2>
              <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed">
                Mehrfamilienhaus Emmerich am Rhein — 47 kWp PV-Anlage, Mieterstrom über G2 vermarktet.
              </p>

              {/* STATS */}
              <div
                className="grid grid-cols-3 mt-8"
                style={{ paddingTop: "32px" }}
              >
                {[
                  { v: "47 kWp", l: "PV-Leistung" },
                  { v: "12 WE", l: "Wohneinheiten" },
                  { v: "~ 38.000 kWh", l: "Jahresproduktion" },
                ].map((s, i) => (
                  <div
                    key={s.l}
                    className="px-4 first:pl-0 last:pr-0"
                    style={{
                      borderLeft: i === 0 ? "none" : "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div
                      className="font-bold text-white leading-tight"
                      style={{ fontSize: "clamp(1.25rem, 2vw, 1.75rem)", letterSpacing: "-0.4px" }}
                    >
                      {s.v}
                    </div>
                    <div className="text-white/55 text-xs md:text-sm mt-2">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA / CROSS-LINK */}
        <section className="relative py-32 md:py-40 px-6 md:px-10 lg:px-14 overflow-hidden bg-[#0A0A0A]">
          <div
            className="absolute inset-0 pointer-events-none opacity-25"
            style={{
              backgroundImage:
                "linear-gradient(rgba(76,175,80,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(76,175,80,0.12) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          <div className="relative max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <h2
                className="font-bold uppercase text-white leading-[0.95] break-words"
                style={{
                  fontSize: "clamp(1.75rem, 7vw, 4.5rem)",
                  letterSpacing: "clamp(-0.6px, -0.18vw, -2.4px)",
                  textShadow: "0 0 32px rgba(76,175,80,0.55)",
                }}
              >
                MEHR ÜBER G2 ERFAHREN
              </h2>
              <p className="mt-6 text-white/80 text-base md:text-lg max-w-xl leading-relaxed">
                Die Software-Ebene über Ihrer Anlage — direkt bei G2 Contracting.
              </p>
              <a
                href="https://g2-contracting.de"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-full text-sm font-medium bg-[#4CAF50] text-[#0A0A0A] shadow-[0_0_28px_rgba(76,175,80,0.5)] hover:shadow-[0_0_40px_rgba(76,175,80,0.7)] transition-all"
              >
                g2-contracting.de besuchen →
                <ExternalLink size={14} />
              </a>
            </div>

            {/* Placeholder Dashboard Visual */}
            <div
              className="overflow-hidden rounded-2xl aspect-[4/3] relative group cursor-pointer"
              style={{
                background: "linear-gradient(135deg, #0A0A0A 0%, #143218 100%)",
                border: "1px solid rgba(76,175,80,0.2)",
                transition: "transform 600ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <span
                  aria-hidden
                  className="inline-block w-2 h-2 rounded-full bg-[#4CAF50] shadow-[0_0_14px_rgba(76,175,80,0.9)]"
                  style={{ animation: "pulse 2s ease-in-out infinite" }}
                />
                <span className="text-white/60 text-sm">Live Dashboard auf g2-contracting.de</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
